import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import { Component, ContentChild, EventEmitter, forwardRef, Input, Optional, Output, SkipSelf, TemplateRef, ViewChild } from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { Observable } from "rxjs";

import { WithConfig } from "ng-zorro-antd/core/config";
import {
  NzFormatBeforeDropEvent,
  NzFormatEmitEvent,
  NzTreeBaseService,
  NzTreeHigherOrderServiceToken,
  NzTreeNode,
  NzTreeNodeKey,
  NzTreeNodeOptions,
} from "ng-zorro-antd/core/tree";
import { InputBoolean } from "ng-zorro-antd/core/util";

import { UiTreeService } from "./services/tree.service";
import { NzTreeComponent } from "ng-zorro-antd/tree";

export function NzTreeServiceFactory(higherOrderService: NzTreeBaseService, treeService: UiTreeService): NzTreeBaseService {
  return higherOrderService ? higherOrderService : treeService;
}

@Component({
  selector: "ui-tree",
  exportAs: "uiTree",
  templateUrl: "tree.component.html",
  providers: [
    UiTreeService,
    {
      provide: NzTreeBaseService,
      useFactory: NzTreeServiceFactory,
      deps: [[new SkipSelf(), new Optional(), NzTreeHigherOrderServiceToken], UiTreeService],
    },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiTreeComponent),
      multi: true,
    },
  ],
})
export class UiTreeComponent extends NzTreeComponent {
  @Input("uiShowIcon") @InputBoolean() @WithConfig() public nzShowIcon = false;
  @Input("uiHideUnMatched") @InputBoolean() @WithConfig() public nzHideUnMatched = false;
  @Input("uiBlockNode") @InputBoolean() @WithConfig() public nzBlockNode = false;
  @Input("uiExpandAll") @InputBoolean() public nzExpandAll = false;
  @Input("uiSelectMode") @InputBoolean() public nzSelectMode = false;
  @Input("uiCheckStrictly") @InputBoolean() public nzCheckStrictly = false;
  @Input("uiShowExpand") @InputBoolean() public nzShowExpand = true;
  @Input("uiShowLine") @InputBoolean() public nzShowLine = false;
  @Input("uiCheckable") @InputBoolean() public nzCheckable = false;
  @Input("uiAsyncData") @InputBoolean() public nzAsyncData = false;
  @Input("uiDraggable") @InputBoolean() public nzDraggable = false;
  @Input("uiMultiple") @InputBoolean() public nzMultiple = false;
  @Input("uiExpandedIcon") public nzExpandedIcon?: TemplateRef<{ $implicit: NzTreeNode; origin: NzTreeNodeOptions }>;
  @Input("uiVirtualItemSize") public nzVirtualItemSize = 28;
  @Input("uiVirtualMaxBufferPx") public nzVirtualMaxBufferPx = 500;
  @Input("uiVirtualMinBufferPx") public nzVirtualMinBufferPx = 28;
  @Input("uiVirtualHeight") public nzVirtualHeight: string | null = null;
  @Input("uiTreeTemplate") public nzTreeTemplate?: TemplateRef<{ $implicit: NzTreeNode; origin: NzTreeNodeOptions }>;
  @Input("uiBeforeDrop") public nzBeforeDrop?: (confirm: NzFormatBeforeDropEvent) => Observable<boolean>;
  @Input("uiData") public nzData: NzTreeNodeOptions[] | NzTreeNode[] = [];
  @Input("uiExpandedKeys") public nzExpandedKeys: NzTreeNodeKey[] = [];
  @Input("uiSelectedKeys") public nzSelectedKeys: NzTreeNodeKey[] = [];
  @Input("uiCheckedKeys") public nzCheckedKeys: NzTreeNodeKey[] = [];
  @Input("uiSearchValue") public nzSearchValue = "";
  @Input("uiSearchFunc") public nzSearchFunc?: (node: NzTreeNodeOptions) => boolean;
  @ContentChild("nzTreeTemplate", { static: true }) public nzTreeTemplateChild!: TemplateRef<{
    $implicit: NzTreeNode;
    origin: NzTreeNodeOptions;
  }>;
  @ViewChild(CdkVirtualScrollViewport, { read: CdkVirtualScrollViewport })
  @Output("uiExpandedKeysChange")
  public readonly nzExpandedKeysChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output("uiSelectedKeysChange") public readonly nzSelectedKeysChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output("uiCheckedKeysChange") public readonly nzCheckedKeysChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output("uiSearchValueChange") public readonly nzSearchValueChange = new EventEmitter<NzFormatEmitEvent>();
  @Output("uiClick") public readonly nzClick = new EventEmitter<NzFormatEmitEvent>();
  @Output("uiDblClick") public readonly nzDblClick = new EventEmitter<NzFormatEmitEvent>();
  @Output("uiContextMenu") public readonly nzContextMenu = new EventEmitter<NzFormatEmitEvent>();
  @Output("uiCheckBoxChange") public readonly nzCheckBoxChange = new EventEmitter<NzFormatEmitEvent>();
  @Output("uiExpandChange") public readonly nzExpandChange = new EventEmitter<NzFormatEmitEvent>();
  @Output("uiOnDragStart") public readonly nzOnDragStart = new EventEmitter<NzFormatEmitEvent>();
  @Output("uiOnDragEnter") public readonly nzOnDragEnter = new EventEmitter<NzFormatEmitEvent>();
  @Output("uiOnDragOver") public readonly nzOnDragOver = new EventEmitter<NzFormatEmitEvent>();
  @Output("uiOnDragLeave") public readonly nzOnDragLeave = new EventEmitter<NzFormatEmitEvent>();
  @Output("uiOnDrop") public readonly nzOnDrop = new EventEmitter<NzFormatEmitEvent>();
  @Output("uiOnDragEnd") public readonly nzOnDragEnd = new EventEmitter<NzFormatEmitEvent>();
}
